import { useCallback, useRef, useState, useEffect } from "react";

import {
  TableMetadata,
  useTable,
  Typography,
  MultiTypeDownloadToolTip,
  Button,
  IconWrapper,
  Tooltip,
  Modal
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import {
  ExportFiltersData,
  ExportTypeEnum,
  ImportantDateEventEnum,
} from "@king-ict/eupisi-ui/models";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  searchInstitutionCriteria,
  searchEnrollmentGroupCriteria,
  deleteInstitutionCriterion,
  deleteEnrollmentGroupCriterion,
  getCriteriaExportFile,
  copyPreviousInstitutionCriterions,
  copyPreviousEnrollmentGroupCriterions
} from "../../../api";
import {
  newCriterionRoute,
  editCriterionUrl,
  useTypedSelector,
} from "../../../app";
import {
  AdministrationListHeader,
  AdministrationListFilters,
  AdministrationList,
  AdministrationListFiltersData,
} from "../../../components";
import { usePermission, useBusinessRule } from "../../../hooks";
import {
  EnrollmentTypeEnum,
  InstitutionCriterion,
  isUserInEnrollmentGroup,
  PermissionEnum,
} from "../../../models";
import styles from "./CriterionList.module.scss";

export const CriterionList = () => {
  const [criteria, setCriteria] = useState<InstitutionCriterion[]>([]);
  const filtersRef = useRef<AdministrationListFiltersData>(
    {} as AdministrationListFiltersData
  );

  const [exportType, setExportType] = useState<number>(ExportTypeEnum.Excel);
  const [isCopyCriterionModalOpened, setIsCopyCriterionModalOpened] = useState(false);

  const { t } = useTranslation();
  const history = useHistory();

  const user = useTypedSelector((state) => state.application.user);

  const importantDate = useTypedSelector(
    (state) => state.application.importantDate
  );
  const { hasPermission } = usePermission();
  const {
    canCreateCriterion,
    canUpdateCriterion,
    canDeleteCriterion,
  } = useBusinessRule();

  const [
    tableProps,
    tableState,
    setTableFilters,
  ] = useTable<AdministrationListFiltersData>({
    data: criteria,
    totalItems: criteria.length,
    noHover: true,
    metadata: {
      rowKey: "id",
      columns: [
        {
          columnKey: "criterionName",
          header: t("criterion.criterion"),
          accessor: "criterionName",
        },
        {
          columnKey: "points",
          header: t("criterion.points"),
          accessor: (request) => {
            return (
              <>
                <Typography variant="baseBold">{``}</Typography>
                <Typography variant="base" color="light">
                  {!request.isDirectEnrollment
                    ? `${request.points}`
                    : `${t("criterion.directEnrollment")}`}
                </Typography>
              </>
            );
          },
        },
      ],
    } as TableMetadata<InstitutionCriterion>,
  });

  const handleFiltersChanged = useCallback(
    async (filters: AdministrationListFiltersData) => {
      filtersRef.current = filters;
      setTableFilters(filters);
    },
    [setTableFilters]
  );

  useEffect(() => {
    (async () => {
      if (tableState.pedagogicalYearId) {
        if (isUserInEnrollmentGroup(user)) {
          setCriteria(await searchEnrollmentGroupCriteria(tableState));
        } else {
          setCriteria(await searchInstitutionCriteria(tableState));
        }
      }
    })();
  }, [tableState, user]);

  const handleAddNewClicked = () => {
    history.push(newCriterionRoute);
  };

  const handleDetailsClicked = (id?: number, guid?: string) => {
    if (id) {
      history.push(editCriterionUrl(id!));
    }
  };

  const handleToggleCopyCriterionModal = () => {
    setIsCopyCriterionModalOpened((prev) => !prev);
  };

  const handleToggleCopyCriterionConfirm = async () => {
    if (isUserInEnrollmentGroup(user)) {
      await copyPreviousEnrollmentGroupCriterions();
    }
    else {
      await copyPreviousInstitutionCriterions();
    }

    handleToggleCopyCriterionModal();
    await handleFiltersChanged({ ...filtersRef.current });
  };

  const handleCopyPreviousClicked = () => {

    handleToggleCopyCriterionModal();
  };

  const handleDeleteConfirmed = async (id: number) => {
    if (isUserInEnrollmentGroup(user)) {
      await deleteEnrollmentGroupCriterion(id);
    } else {
      await deleteInstitutionCriterion(id);
    }

    await handleFiltersChanged({ ...filtersRef.current });

    return true;
  };

  const handleChangeFormat = (typeId: number) => {
    setExportType(typeId);
  };

  const handleDownloadDocument = () => {
    getCriteriaExportFile({
      pedagogicalYearId: filtersRef.current.pedagogicalYearId,
      exportTypeId: exportType,
    } as ExportFiltersData);
  };

  return hasPermission(PermissionEnum.CriterionRead) ? (
    <>
      <AdministrationListHeader
        infoMessage={
          user.enrollmentGroupId
            ? t("criterion.listInfoMessageSR")
            : t("criterion.listInfoMessageDV")
        }
        title={t("criterion.listTitle")}
      />

      <Row>
        <Col md={9}>
          <AdministrationListFilters
            isFilteredByYear
            callbackOnFiltersChanged
            onFiltersChanged={handleFiltersChanged}
          />
        </Col>
        {(canCreateCriterion(filtersRef.current.pedagogicalYearId || 0) && (!criteria || criteria.length === 0)) &&
          <Col md={3}>
            <Tooltip
              overlay={<div className={styles.infoTooltip}>{t("criterion.copyPreviousTooltip")}</div>}
              placement="left"
            >
              <IconWrapper color="primary">
                <InfoIcon width={20} height={40} viewBox={"0 0 20 20"} fill={"none"} />
              </IconWrapper>
            </Tooltip>
            <Button
              className={styles.addNewButton}
              onClick={handleCopyPreviousClicked}
            >
              {t("criterion.copyPrevious")}
            </Button>
          </Col>
        }
      </Row>
      <AdministrationList
        tableProps={tableProps}
        isCreateEnabled={canCreateCriterion(
          filtersRef.current.pedagogicalYearId || 0
        )}
        isDeleteEnabled={canDeleteCriterion(
          filtersRef.current.pedagogicalYearId || 0
        )}
        isDetailsEnabled={canUpdateCriterion(
          filtersRef.current.pedagogicalYearId || 0
        )}
        deleteActionText={t("criterion.deleteActionText")}
        deleteModalTitle={t("criterion.deleteModalTitle")}
        deleteAndUpdateAllowedByDate={
          importantDate.enrollmentTypeId ===
          EnrollmentTypeEnum.SubsequentEnrollment &&
          importantDate.eventId ===
          ImportantDateEventEnum.InstitutionAdministration
        }
        deleteModalMessage={t("criterion.deleteModalMessage")}
        onAddNewClicked={handleAddNewClicked}
        onDeleteConfirmed={handleDeleteConfirmed}
        onDetailsClicked={handleDetailsClicked}
      />

      {criteria?.length > 0 && (
        <MultiTypeDownloadToolTip
          exportType={exportType}
          onclick={handleDownloadDocument}
          changeFormat={handleChangeFormat}
        />
      )}

      <Modal
        type={"confirm"}
        title={t("criterion.copyPreviousModalTitle")}
        cancelText={t("common.no")}
        confirmText={t("common.yes")}
        open={isCopyCriterionModalOpened}
        onClose={handleToggleCopyCriterionModal}
        onConfirm={handleToggleCopyCriterionConfirm}
      >
        {t("criterion.copyPreviousModalMessage")}
      </Modal>
    </>
  ) : null;
};
